import { type ReactNode } from "react";
import { modules, useDashboardLayoutRedesignEnabled } from "byzantine";
import type {
  WebSectionId,
  WebSectionLeft,
  WebSectionRight,
} from "byzantine/src/dbbl/businessLogic/modules/userDisplayPreferences/types";

import AccountBalances from "./AccountBalances";
import AccountTotalsCard from "./AccountTotals/AccountTotalsCard";
import ChartCard from "./ChartCard";
import CardOffers from "./CardOffers";
import ExternalAccountTiles from "./ExternalAccountTiles";
import MarketingCardContainer from "./MarketingCardContainer";
import MarketingImage from "./MarketingImage";
import RecentTransactionsCard from "./RecentTransactionsCard";
import RecentTransactionsLedgerCard from "./RecentTransactionsLedgerCard";
import RecentTransactionsLedgerModule from "./RecentTransactionsLedgerModule";
import RecentTransactionsModule from "./RecentTransactionsModule";
import ServicesCard from "./ServicesCard";
import SubscriptionCard from "../subscriptions/SubscriptionCard";
import ArrayPlusWidget from "../ArrayPlus/ArrayPlusWidget";
import SavvyMoneyWidget from "../savvy_money/SavvyMoneyWidget";
import {
  MARKETING_BANNER_IMAGE_ADDRESS,
  MARKETING_CARD_IMAGE_ADDRESS,
} from "./constants";
import ExternalAccountsContainer from "../../src/modules/dashboard/ExternalAccounts/ExternalAccountsContainer/ExternalAccountsContainer";
import QuickTransferCard from "./QuickTransferCard";
import type {
  DashboardSectionContext,
  SettledTransactionsState,
} from "./types";
import BalanceChart from "./Charts/BalanceChart";
import AccountsModule from "../../src/modules/accounts/AccountsModule";
import { useUserFeatures } from "../contexts/UserFeaturesContext";
import NetWorthWidgetCard from "./NetWorthWidget/NetWorthWidgetCard";

const { SECTION } = modules.userDisplayPreferences;

export const RecentTransactionsSection = ({
  replyTime,
  settledTransactionsState,
}: {
  replyTime: string;
  settledTransactionsState: SettledTransactionsState;
}) => {
  const isDashboardLayoutRedesignEnabled = useDashboardLayoutRedesignEnabled();
  const { live_ledger: isLiveLedgerEnabled } = useUserFeatures();
  if (isDashboardLayoutRedesignEnabled) {
    if (isLiveLedgerEnabled) {
      return <RecentTransactionsLedgerModule replyTime={replyTime} />;
    }
    return (
      <RecentTransactionsModule
        replyTime={replyTime}
        settledTransactionsState={settledTransactionsState}
      />
    );
  }
  if (isLiveLedgerEnabled) {
    return <RecentTransactionsLedgerCard replyTime={replyTime} />;
  }
  return <RecentTransactionsCard replyTime={replyTime} />;
};

export const renderLeftSection = (
  id: WebSectionLeft,
  ctx: DashboardSectionContext
): ReactNode[] => {
  switch (id) {
    case SECTION.MARKETING_BANNER_IMAGE:
      return [
        <MarketingImage
          key="marketing_banner_image"
          address={MARKETING_BANNER_IMAGE_ADDRESS}
        />,
      ];
    case SECTION.BALANCE_CHARTS: {
      if (ctx.features.enable_dashboard_layout_redesign) {
        return [
          <BalanceChart
            key="balance_charts"
            settledTransactionsState={ctx.settledTransactionsState}
          />,
        ];
      }
      return [];
    }
    case SECTION.ACCOUNTS:
      return ctx.features.enable_dashboard_layout_redesign
        ? [
            <AccountsModule
              key="accounts"
              additionalExternalAccounts={ctx.externalAccounts}
            />,
          ]
        : [<AccountBalances key="accounts" />];
    case SECTION.EXTERNAL_ACCOUNTS:
      return [
        <ExternalAccountsContainer
          key="external_accounts"
          externalAccounts={ctx.externalAccounts}
          uatMicrodepAmntOne={ctx.uatMicrodepAmntOne}
          uatMicrodepAmntTwo={ctx.uatMicrodepAmntTwo}
        />,
      ];
    case SECTION.RECENT_TRANSACTIONS:
      return [
        <RecentTransactionsSection
          key="recent_transactions"
          replyTime={ctx.replyTime}
          settledTransactionsState={ctx.settledTransactionsState}
        />,
      ];
    case SECTION.CARD_OFFERS:
      return [<CardOffers key="card_offers" />];
    default:
      return [];
  }
};

export const renderRightSection = (
  id: WebSectionRight,
  ctx: DashboardSectionContext
): ReactNode[] => {
  switch (id) {
    case SECTION.ACCOUNT_TOTALS: {
      // This is replaced in the new dashboard layout redesign by SECTION.BALANCE_CHARTS
      if (!ctx.features.enable_dashboard_layout_redesign) {
        return [<AccountTotalsCard key="account_totals" />];
      }
      return [];
    }
    case SECTION.CHART_CARD:
      return [
        <ChartCard
          key="chart_card"
          borderCard={!!ctx.features.enable_dashboard_layout_redesign}
        />,
      ];
    case SECTION.NET_WORTH: {
      // The native widget only belongs on the redesigned dashboard.
      if (
        !ctx.features.enable_net_worth_widget ||
        !ctx.features.enable_dashboard_layout_redesign
      )
        return [];
      return [<NetWorthWidgetCard key="net_worth_widget" borderCard />];
    }
    case SECTION.CREDIT_MONITORING: {
      const nodes: ReactNode[] = [];
      if (ctx.features.array_plus_credit_reporting) {
        nodes.push(<ArrayPlusWidget key="array_plus" />);
      }
      if (ctx.features.olb_enable_credit_monitor) {
        nodes.push(
          <SavvyMoneyWidget
            key="savvy_money"
            hasErrored={ctx.hasSavvyWidgetErrored}
            refetch={ctx.fetchSavvyMoneyRedirectUrl}
            isLoading={ctx.isSavvyFetchLoading}
            borderCard={!!ctx.features.enable_dashboard_layout_redesign}
          />
        );
      }
      return nodes;
    }
    case SECTION.VERIFY_EXTERNAL_ACCOUNTS: {
      if (!ctx.features.enable_dashboard_layout_redesign) {
        return [
          <ExternalAccountTiles
            key="verify_external_accounts"
            externalAccounts={ctx.externalAccounts}
            uatMicrodepAmntOne={ctx.uatMicrodepAmntOne}
            uatMicrodepAmntTwo={ctx.uatMicrodepAmntTwo}
          />,
        ];
      }
      return [];
    }
    case SECTION.MARKETING_CARD:
      return [<MarketingCardContainer key="marketing_card" />];
    case SECTION.SERVICES:
      return ctx.servicesApps
        ? [<ServicesCard key="services" services={ctx.servicesApps} />]
        : [];
    case SECTION.SUBSCRIPTIONS:
      return ctx.isSubscriptionEnabled
        ? [<SubscriptionCard key="subscriptions" />]
        : [];
    case SECTION.MARKETING_CARD_IMAGE:
      return [
        <MarketingImage
          key="marketing_card_image"
          address={MARKETING_CARD_IMAGE_ADDRESS}
        />,
      ];
    case SECTION.QUICK_TRANSFER:
      return [<QuickTransferCard key="quick_transfer" />];
    default:
      return [];
  }
};

export const renderSection = (
  id: WebSectionId,
  ctx: DashboardSectionContext
): ReactNode[] => {
  const leftSection = renderLeftSection(id as WebSectionLeft, ctx);

  return leftSection.length
    ? leftSection
    : renderRightSection(id as WebSectionRight, ctx);
};
